import { useState } from 'react'
import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { useLocation } from 'react-router'
import { useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { useDialogControllers, Button as DSButton, Box } from '@machobear/design-system'
import { Userpilot } from 'userpilot'
import config from '@/config'
import useCurrentSession from '@/hooks/use-current-session'
import { unlock, view } from '@/models/plans/apis'
import { Button } from '@/components/button'
import Skeleton from '@/components/skeleton'
import { useGetProduct } from '@/hooks/use-block-and-pay'
import checkCredits from '@/utils/check-credits'
import { Checkmark, TrustScoreLockedSmall } from '@/components/icons'
import AnimatedTrustScore from '@/features/Application/animated-trust-score'
import { fontWeights, colors, fontSizes, breakpoints } from '@/styles'
import { showSampleModal } from '@/features/Application/sample-modal'
import { useAvailableCreditChecks } from '@/hooks/use-block-and-pay'
import useGetCreditReports from '@/hooks/use-get-credit-report'
import { PaymentProvider } from '@/components/ds-checkout-modal/payment-context'
import NoBillingUserDialog from '@/features/SponsoredListing/modals/no-billing-user-modal'
import { pushToDatalayer } from '@/utils/push-datalayer'
import { TrustScorePaymentModal } from './trust-score-payment-modal'
import LockedLeftColumn from './locked-left-column'

const { APP_PATH } = config

const tabletViewBreakpoint = '1400px'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 32px 20px;
  margin-bottom: 40px;
  background-color: ${colors.white};
  box-shadow:
    0px 1px 3px rgba(0, 0, 0, 0.1),
    0px 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 6px;
`

const InnerContainer = styled.div`
  display: flex;
  align-items: ${props => (props.alignTop ? 'flex-start' : 'center')};
  gap: 10px;

  @media screen and (max-width: ${tabletViewBreakpoint}) {
    ${props =>
      !props.isLocked &&
      `
      flex-direction: column;
      align-items: center;
      gap: 0;
    `}
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}),
    screen and (min-width: ${breakpoints.desktopSmall}) and (max-width: ${tabletViewBreakpoint}) {
    flex-direction: column;
    align-items: center;
    gap: 0;
  }
`

const AnimatedScoreWrapper = styled.div`
  margin-right: 40px;
  @media screen and (max-width: ${tabletViewBreakpoint}) {
    margin-right: 0;
  }
`

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${props => props.alignSelf && `align-self: ${props.alignSelf}`};

  ${props =>
    props.isLocked &&
    `
      @media screen and (max-width: ${breakpoints.ipadMiniMax}),
      screen and (min-width: ${breakpoints.desktopSmall}) and (max-width: ${tabletViewBreakpoint}) {
        margin-top: 32px;
        flex-direction: row;
        justify-content: center;
      }
  `}
`

const Title = styled.span`
  font-weight: ${fontWeights.medium};
  font-size: 20px;
  line-height: 28px;
  color: ${colors.grey16};
  margin-bottom: 12px;
  @media screen and (max-width: ${tabletViewBreakpoint}) {
    font-size: 16px;
    margin-top: 12px;
  }
`

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.grey16};
  margin: 0;
  padding: ${({ padding }) => (padding ? padding : '0px')};
  width: 100%;
  margin-bottom: ${({ marginBottom }) => (marginBottom ? marginBottom : '0px')};

  @media screen and (max-width: ${tabletViewBreakpoint}) {
    font-size: 14px;
  }
`

const Link = styled.a`
  text-decoration: none;
  color: ${colors.link};
  cursor: pointer;
`

const SmallLink = styled(Link)`
  font-size: 14px;
  font-weight: ${fontWeights.medium};
`

const ReasonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 8px;
  padding: 12px 16px;
  background-color: ${colors.grey96};
  margin-bottom: 20px;
  border-radius: 6px;
`

const ReasonTitle = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 20px;

  color: ${colors.grey16};
`

const ReasonText = styled.span`
  font-style: italic;
  line-height: 20px;
  font-size: 14px;
  color: ${colors.grey16};
`

const NameTag = styled(ReasonText)`
  font-style: normal;
  width: 100%;
  text-align: right;
`

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: max-content max-content;
  grid-gap: 20px;
  margin-top: 24px;

  @media screen and (max-width: ${tabletViewBreakpoint}) {
    grid-template-columns: ${({ isSingle }) => (isSingle ? '1fr' : '1fr 1fr')};
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: 1fr;
    grid-gap: 16px;
    > :nth-child(2) {
      order: -1;
    }
  }
`
const Bold = styled.span`
  font-weight: ${fontWeights.bold};
`

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  @media screen and (max-width: ${tabletViewBreakpoint}) {
    margin-top: 16px;
  }
`
const NoCreditHistoryFooter = styled.span`
  color: ${colors.grey40};
  font-weight: ${fontSizes.medium};
  font-size: 12px;
  margin-top: 20px;
  text-align: left;
`

const StyledButton = styled(Button)`
  margin: 20px 0 12px;
  width: 100% !important;
  max-width: 275px !important;
  padding: 0;
  font-size: 12px;
`

const RightFooterText = styled.span`
  font-size: ${fontSizes.small};
  color: ${colors.grey40};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const IncludesText = styled.div`
  font-size: 15px;
  text-align: left;
  color: ${colors.grey24};
`

const Row = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 9px;
  font-size: 15px;
`

const BulletPoints = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-wrap: nowrap;
`

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: flex-end;
  padding: 20px;
  border: 1px solid ${colors.blue80};
  border-radius: 6px;
  background-color: ${colors.blue96};
  width: 315px;
  flex-wrap: nowrap;
`

const ButtonLinkText = styled.a`
  color: ${colors.link};
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-align: left;
  border-bottom: 1px solid ${colors.link};

  &:hover {
    cursor: pointer;
  }
`
const ListItemWrapper = styled.ul`
  padding-left: 25px;
`

const TrustScoreSummary = ({
  score,
  state,
  status,
  rate,
  applicationId,
  reason,
  applicantName,
  hasUnlockCredits,
  applicantFullName,
  isLoading,
  requestQueued
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const dispatch = useDispatch()
  const session = useCurrentSession()
  const { data: products } = useGetProduct('credit-check-single-package')
  const { visible, showModal, closeModal } = useDialogControllers({
    trustScore: false,
    editPaymentDialog: false,
    trustScoreAgreementDialog: false,
    trustScoreAgreementNoDataDialog: false,
    noBillingUserDialog: false
  })
  const { error, isLoading: isCreditReportsLoading } = useGetCreditReports(applicationId)

  const [submitting, setSubmitting] = useState(false)
  const client = useQueryClient()
  const applicationDetails = {
    name: applicantFullName,
    id: applicationId
  }
  const showLoading = isLoading || isCreditReportsLoading
  const isAnalyzing = state === 'analyzing'
  const isLocked = state === 'locked'
  const isRequestQueued = requestQueued === true
  const trustScoreDialogId =
    error?.message_key === 'credit_report_submitted_data_not_found'
      ? 'trustScoreAgreementNoDataDialog'
      : 'trustScoreAgreementDialog'
  const { availableCreditChecks, creditDetails } = useAvailableCreditChecks()

  const currentCount = availableCreditChecks >= 0 ? availableCreditChecks : '0'

  const getNewTrustScore = async () => {
    client.invalidateQueries({ queryKey: ['block'] })
    await dispatch.application.getApplicationTrustScore(applicationId)
  }

  const unlockTrustScore = async () => {
    setSubmitting(true)
    try {
      await unlock(applicationDetails.id, {
        unlocked: 1,
        viewed: 1,
        source: checkCredits(creditDetails)
      })
      pushToDatalayer({
        event: 'application-trust-score-submit-purchase-credit'
      })

      Userpilot.track('goal_purchase_credits_trust_score_unlock')
      localStorage.removeItem('promoType')
      client.invalidateQueries({ queryKey: ['blocks'] })
      getNewTrustScore()
    } catch (err) {
      setSubmitting(false)
      throw err
    }
    setSubmitting(false)
  }

  const viewScore = async () => {
    setSubmitting(true)
    try {
      await view(applicationDetails.id, {
        source: checkCredits(creditDetails)
      })
      getNewTrustScore()
    } catch (err) {
      throw err
    }
    setSubmitting(false)
  }

  const documentsUrl = `${location?.pathname?.substring(
    0,
    location?.pathname?.lastIndexOf('/')
  )}/${'documents'}`
  const handleDocumentsClick = () => window.open(`${APP_PATH}${documentsUrl}`, '_blank')

  const commonBulletPoints1 = (
    <BulletPoints>
      <IncludesText>Includes:</IncludesText>
      <Row>
        <Checkmark />
        {t('l.application.view.credit_summary.link', 'Credit Summary')}
      </Row>
      <Row>
        <Checkmark />
        {t('l.application.view.risk_assessment.title', 'Risk Assessment')}
      </Row>
      <Row>
        <Checkmark />
        {t('l.application.view.court_record_verification.subtitle', 'Court Record Verification')}
      </Row>
      <Row>
        <Checkmark />
        {t('l.application.view.income_verification.subtitle', 'Income Verification')}
      </Row>
      <Row>
        <Checkmark />
        {t('common.and_more', 'And more')}
      </Row>
    </BulletPoints>
  )

  const commonBulletPoints2 = (
    <BulletPoints>
      <IncludesText>Includes:</IncludesText>
      <Row>
        <Checkmark />
        {t('l.application.view.financial_documents.title', 'Financial Documents')}
      </Row>
      <Row>
        <Checkmark />
        {t('r.profile.edit.basic_information.title', 'Basic Information')}
      </Row>
      <Row>
        <Checkmark />
        {t('l.application.view.references.title', 'References')}
      </Row>
      <Row>
        <Checkmark />
        {t('l.application.view.income_verification.subtitle', 'Income Verification')}
      </Row>
      <Row>
        <Checkmark />
        {t('common.and_more', 'And more')}
      </Row>
    </BulletPoints>
  )

  const viewSampleReportButton = (
    <ButtonLinkText
      id="application-trust-score-header-view-exp"
      theme="secondary"
      onClick={showSampleModal}
      width="100%"
    >
      {t('l.application.unlockmodal.viewsample.button', 'View a sample report')}
    </ButtonLinkText>
  )

  const UnlockButton = ({ children, onClick }) => (
    <StyledButton
      id={
        hasUnlockCredits
          ? 'application-trust-score-submit-purchase-credit'
          : 'application-trust-score-header-unlock'
      }
      minHeight="40px"
      minWidth="100px"
      padding="12px 16px"
      theme="primary"
      disabled={submitting}
      onClick={onClick}
    >
      {children}
    </StyledButton>
  )

  const trustScoreReportsRemaining = (
    <RightFooterText>
      <strong style={{ marginRight: '4px' }}>
        {availableCreditChecks === 0 ? '0' : currentCount}
      </strong>
      {t('l.application.view.trust_score_reports_remaining', 'Trust Score Reports remaining')}
    </RightFooterText>
  )

  const openModal = () => {
    const isBillingUserValid = session?.billing_user_valid
    showModal(isBillingUserValid ? trustScoreDialogId : 'noBillingUserDialog')()
  }

  const LOCKED_ACTIVE = (
    <RightColumn>
      {commonBulletPoints1}
      <Box style={{ margin: '20px auto', width: '100%' }}>
        <DSButton
          loading={isRequestQueued && status === 'not_enough_info'}
          icon={<TrustScoreLockedSmall />}
          onClick={openModal}
          disabled={submitting}
          data={{ testId: 'trust-score-unlock-locked-active' }}
        >
          {isRequestQueued && status === 'not_enough_info'
            ? t('l.trust_scire_retrieving_button_text', 'Retrieving')
            : t('l.application.view.unlock.button', 'Unlock Trust Score')}
        </DSButton>
      </Box>
      {trustScoreReportsRemaining}
    </RightColumn>
  )

  const CONTENT = {
    locked: {
      active: LOCKED_ACTIVE,
      failed: LOCKED_ACTIVE,
      not_enough_info: (
        <RightColumn>
          {commonBulletPoints2}
          <Box style={{ margin: '20px auto', width: '100%' }}>
            <DSButton
              {...(isRequestQueued && status === 'not_enough_info' ? { loading: true } : '')}
              icon={<TrustScoreLockedSmall />}
              onClick={openModal}
              disabled={submitting}
              data={{ testId: 'trust-score-unlock-locked-not_enough_info' }}
            >
              {isRequestQueued && status === 'not_enough_info'
                ? t('l.trust_scire_retrieving_button_text', 'Retrieving')
                : t('l.application.view.unlock.button', 'Unlock Trust Score')}
            </DSButton>
          </Box>
          {trustScoreReportsRemaining}
        </RightColumn>
      ),
      no_credit_history: (
        <RightColumn>
          {commonBulletPoints1}
          <Box style={{ margin: '20px auto', width: '100%' }}>
            <UnlockButton onClick={viewScore}>
              {t('l.application.view.trust_score.button', 'View Trust Score')}
            </UnlockButton>
          </Box>
          {trustScoreReportsRemaining}
        </RightColumn>
      )
    },
    unlocked: {
      not_enough_info: (
        <>
          <Title>
            {t(
              'l.application.view.not_enough_info_unlocked.new_title',
              'Missing credit check details'
            )}
          </Title>
          <Paragraph marginBottom="24px">
            <Trans i18nKey="l.application.view.not_enough_info_unlocked.new_text">
              We were <Bold>unable to retrieve</Bold> the tenant’s information from Equifax®.
              Please ask the applicant to resubmit their credit check details.
            </Trans>
          </Paragraph>
          <Paragraph>
            <p>
              {t(
                'l.application.view.not_enough_info_unlocked.new_text_leading',
                'You can still view the following financial information:'
              )}
            </p>
            <ListItemWrapper>
              <li>
                {t(
                  'l.application.view.not_enough_info_unlocked.new_text_bullet1',
                  'Verified employment documents'
                )}
              </li>
              <li>
                {t(
                  'l.application.view.not_enough_info_unlocked.new_text_bullet2',
                  'Verified income documents'
                )}
              </li>
              <li>
                {t(
                  'l.application.view.not_enough_info_unlocked.new_text_bullet3',
                  'Court record verification'
                )}
              </li>
              <li>
                {t(
                  'l.application.view.not_enough_info_unlocked.new_text_bullet4',
                  'Rent-to-income ratio'
                )}
              </li>
            </ListItemWrapper>
          </Paragraph>
          <SmallLink onClick={handleDocumentsClick}>
            {t('l.application.view.view_documents.link', 'View Documents')}
          </SmallLink>
        </>
      ),
      failed: (
        <>
          <Title>
            {t(
              'l.application.view.not_enough_info_unlocked.new_title',
              'Missing credit check details'
            )}
          </Title>
          <Paragraph marginBottom="24px">
            <Trans i18nKey="l.application.view.not_enough_info_unlocked.new_text">
              We were <Bold>unable to retrieve</Bold> the tenant’s information from Equifax®.
              Please ask the applicant to resubmit their credit check details.
            </Trans>
          </Paragraph>
          <Paragraph>
            <p>
              {t(
                'l.application.view.not_enough_info_unlocked.new_text_leading',
                'You can still view the following financial information:'
              )}
            </p>
            <ListItemWrapper>
              <li>
                {t(
                  'l.application.view.not_enough_info_unlocked.new_text_bullet1',
                  'Verified employment documents'
                )}
              </li>
              <li>
                {t(
                  'l.application.view.not_enough_info_unlocked.new_text_bullet2',
                  'Verified income documents'
                )}
              </li>
              <li>
                {t(
                  'l.application.view.not_enough_info_unlocked.new_text_bullet3',
                  'Court record verification'
                )}
              </li>
              <li>
                {t(
                  'l.application.view.not_enough_info_unlocked.new_text_bullet4',
                  'Rent-to-income ratio'
                )}
              </li>
            </ListItemWrapper>
          </Paragraph>
          <SmallLink onClick={handleDocumentsClick}>
            {t('l.application.view.view_documents.link', 'View Documents')}
          </SmallLink>
        </>
      ),
      no_credit_history: (
        <>
          <Title>
            {t('l.application.view.no_credit_history_canada.title', 'No credit history available')}
          </Title>
          <Paragraph marginBottom="20px">
            <Trans i18nKey="l.application.view.credit_history_canada.text">
              This renter <Bold>doesn't yet have a credit history in Canada</Bold>. You can still
              view their reason and financial documents below.
            </Trans>
          </Paragraph>
          <ReasonContainer>
            <ReasonTitle>{t('l.application.view.reason.title', 'Reason')}:</ReasonTitle>
            <ReasonText>"{reason || 'N/A'}"</ReasonText>
            <NameTag>{`- ${applicantName}`}</NameTag>
          </ReasonContainer>
          <SmallLink onClick={handleDocumentsClick}>
            {t('l.application.view.view_documents.link', 'View Documents')}
          </SmallLink>
        </>
      ),
      poor: (
        <>
          <Title>
            {t('l.application.view.proceed_with_caution.title', 'Proceed with caution')}
          </Title>
          <Paragraph>
            {t(
              'l.application.view.proceed_with_caution_summary.text',
              'This applicant may have a history of not paying bills on time. We recommend that you request additional documents that prove their financial suitability.'
            )}
          </Paragraph>
        </>
      ),
      average: (
        <>
          <Title>{t('l.application.view.average.title', 'Room for improvement')}</Title>
          <Paragraph>
            {t(
              'l.application.view.average_summary.text',
              'This applicant has a fair financial history, though they might have missed payments in the past. You may want to request additional documents that prove their financial suitability.'
            )}
          </Paragraph>
        </>
      ),
      very_good: (
        <>
          <Title>{t('l.application.view.very_good.title', 'Low-risk renter')}</Title>
          <Paragraph>
            {t(
              'l.application.view.very_good_summary.text',
              'You should have very little to worry about with this applicant when it comes to receiving rent payments on time.'
            )}
          </Paragraph>
        </>
      ),
      excellent: (
        <>
          <Title>{t('l.application.view.excellent.title', 'Rent with confidence')}</Title>
          <Paragraph>
            {t(
              'l.application.view.excellent_summary.text',
              'This applicant has a strong financial history and very few or no missed payments. They should make a great tenant!'
            )}
          </Paragraph>
        </>
      )
    },

    analyzing: (
      <>
        <Title>{t('l.application.view.analyzing_summary.title', 'Trust Score analyzing')}</Title>
        <Paragraph>
          {t(
            'l.application.view.analyzing_summary.text',
            "We're still analyzing this applicant's information in order to calculate an accurate Trust Score. Please check back shortly."
          )}
        </Paragraph>
        <ButtonContainer isSingle>
          <Button
            id="application-trust-score-header-analyze-view-exp "
            minHeight="48px"
            minWidth="100px"
            padding="12px 16px"
            theme="secondary"
            onClick={showSampleModal}
          >
            {t('l.application.view.view_example.button', 'View Example')}
          </Button>
        </ButtonContainer>
      </>
    )
  }
  const getContent = () => {
    if (isRequestQueued) return CONTENT['locked']['not_enough_info']
    switch (state) {
      case 'locked':
        return CONTENT['locked'][status]
      case 'unlocked':
        return status === 'no_credit_history' || status === 'not_enough_info'
          ? CONTENT['unlocked'][status]
          : CONTENT['unlocked'][rate]
      default:
        return CONTENT['analyzing']
    }
  }
  const animatedScoreWrapper = (
    <AnimatedScoreWrapper>
      <AnimatedTrustScore
        isLocked={isRequestQueued ? true : isLocked}
        isAnalyzing={isAnalyzing}
        score={score}
        rate={rate}
        applicationId={applicationId}
        isLoading={showLoading}
      />
    </AnimatedScoreWrapper>
  )

  const alignTop = isLocked || status === 'no_credit_history' || status === 'no_credit_history'

  return (
    <Container>
      <InnerContainer alignTop={alignTop} isLocked={isLocked}>
        {isLocked || isRequestQueued ? (
          <LockedLeftColumn
            t={t}
            status={status}
            animatedScoreWrapper={animatedScoreWrapper}
            viewSampleReportButton={viewSampleReportButton}
          />
        ) : (
          animatedScoreWrapper
        )}
        <RightContainer isLocked={isLocked} alignSelf={showLoading ? 'flex-start' : 'center'}>
          {!showLoading ? (
            getContent()
          ) : (
            <SkeletonWrapper>
              <Skeleton width={200} height={28} top={12} bottom={12} />
              <Skeleton width="100%" height={72} />
            </SkeletonWrapper>
          )}
        </RightContainer>
      </InnerContainer>
      {status === 'no_credit_history' && (
        <NoCreditHistoryFooter>
          {t(
            'l.application.view.no_credit_disclaimer.text',
            '*The Trust Score helps us support renters of all backgrounds, including newcomers to Canada, by looking beyond their Canadian credit score.'
          )}
        </NoCreditHistoryFooter>
      )}
      <PaymentProvider>
        <TrustScorePaymentModal
          products={products}
          visible={visible}
          showModal={showModal}
          closeModal={closeModal}
          getNewTrustScore={getNewTrustScore}
          applicationId={applicationId}
          hasUnlockCredits={hasUnlockCredits}
          unlockTrustScore={unlockTrustScore}
        />
      </PaymentProvider>
      <NoBillingUserDialog
        open={visible.noBillingUserDialog}
        onClose={closeModal('noBillingUserDialog')}
      />
    </Container>
  )
}

export default TrustScoreSummary
